import type { BlockStateDefinition } from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type {
  BlockStateUnionType,
  BlockType,
  ComponentTypeUnionType,
} from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import { lazy } from 'react';
import type { DatePickerComponentType } from '@unifyapps/defs/blocks/DatePicker/types';
import { isDynamicValue } from '@unifyapps/carbon/no-code/utils/dynamicBindings';
import { parseDate } from '@unifyapps/carbon/widgets/DateTimeWidget/utils';

const importDatePickerComponent = () =>
  import(
    /* webpackChunkName: "date-picker-block" */
    './DatePicker'
  );

const DatePicker = lazy(importDatePickerComponent);

class DatePickerBlockStateDefinition implements BlockStateDefinition {
  getComponent() {
    return DatePicker;
  }

  get type(): ComponentTypeUnionType {
    return 'Calendar';
  }

  get initialStateGetter() {
    return (block: BlockType) =>
      ({
        id: block.id,
        /**
         * We do not have the computed value of defaultDate here,
         * Hence when it is dynamic, the component will handle the setting of the selectedDate in an effect
         */
        selectedDate:
          (block.component as DatePickerComponentType).content.defaultDate &&
          !isDynamicValue((block.component as DatePickerComponentType).content.defaultDate)
            ? parseDate((block.component as DatePickerComponentType).content.defaultDate)
            : new Date(),
        selectedRange: {
          startDate: (block.component as DatePickerComponentType).content.defaultStartDate
            ? parseDate((block.component as DatePickerComponentType).content.defaultStartDate)
            : new Date(),
          endDate: (block.component as DatePickerComponentType).content.defaultStartDate
            ? parseDate((block.component as DatePickerComponentType).content.defaultEndDate)
            : new Date(),
        },
        ..._pick(block.component as DatePickerComponentType, [
          'content',
          'appearance',
          'componentType',
        ]),
      }) as BlockStateUnionType;
  }

  getSlotOrder(block: BlockType) {
    return {
      order: ['header', 'footer'],
    };
  }

  preload() {
    return {
      blockPreload: importDatePickerComponent,
    };
  }
}

export default DatePickerBlockStateDefinition;
